import { useState } from 'react';
import { ChevronDown, ChevronRight, BookOpen } from 'lucide-react';

interface Topic {
  id: string;
  name: string;
}

interface LinkedTopicType {
  id: string;
  selectedTopicIds: string[];
  topicType: {
    label: string;
    topics: Topic[];
  };
}

interface Props {
  linkedTopicTypes: LinkedTopicType[];
}

export default function TopicTypesReadOnlyView({ linkedTopicTypes }: Props) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (linkedTopicTypes.length === 0) {
    return (
      <div className="text-center py-8 border border-dashed border-gray-200 rounded-lg">
        <BookOpen className="w-8 h-8 text-gray-300 mx-auto mb-2" />
        <p className="text-gray-500 text-sm">No topic categories configured.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {linkedTopicTypes.map(item => {
        const isExpanded = expandedIds.has(item.id);
        const selected = item.selectedTopicIds;
        const total = item.topicType.topics.length;

        return (
          <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleExpand(item.id)}
              className="w-full flex items-center gap-2 px-4 py-3 bg-white hover:bg-gray-50 transition-colors text-left"
            >
              {isExpanded
                ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
              }
              <span className="font-medium text-gray-900">{item.topicType.label}</span>
              <span className="text-xs text-gray-400 ml-1">{selected.length} / {total} selected</span>
            </button>

            {isExpanded && (
              <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                {item.topicType.topics.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No active topics.</p>
                ) : (
                  <div className="space-y-2">
                    {item.topicType.topics.map(topic => (
                      <div key={topic.id} className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                          selected.includes(topic.id)
                            ? 'bg-blue-600 border-blue-600'
                            : 'border-gray-300 bg-white'
                        }`}>
                          {selected.includes(topic.id) && (
                            <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 10">
                              <path d="M1.5 5L3.5 7.5L8.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </div>
                        <span className={`text-sm ${selected.includes(topic.id) ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                          {topic.name}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}



